import { css } from '@emotion/core'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import parse from 'html-react-parser'
import * as React from 'react'

import { ctaInlineBreakPoint, longCopyBreakPoint } from '../../constants/breakPoints'
import { breakpoints } from '../../constants/breakPoints'
import { getSingularForm, isMobile } from '../../helpers/common'
import { getCtaEbookLpUrl } from '../../helpers/cta'
import { CTAEbookProps } from '../../interfaces/cta'
import Block from '../Block'
import { Button } from '../Button'
import Image from '../Image'
import { Link } from '../Link'

const CTAEbook: React.FunctionComponent<CTAEbookProps> = ({
  educationResourceUrl = 'product-management',
  isCtaForGuidePage = false,
  isSidebarCta = false,

  cssStyles,
}) => {
  // hide sidebar CTA on screens < longCopyBreakPoint (2 columns only - tablet & mobile: 1 column)
  if (isSidebarCta && isMobile(longCopyBreakPoint)) {
    return null
  }

  const { educationResources } = useStaticQuery(graphql`
    {
      educationResources: allContentfulEducationResource(filter: { ctaImage: { title: { ne: null } } }) {
        edges {
          node {
            educationCategory {
              id
              name
            }
            slug
            title
            ctaTitle
            ctaImage {
              gatsbyImageData(width: 250, quality: 90)
            }
          }
        }
      }
    }
  `)

  const [
    {
      node: { educationCategory, slug, ctaTitle, title, ctaImage },
    },
  ] = educationResources.edges.filter(
    (resource: {
      node: {
        slug: string
      }
    }) => resource.node.slug === educationResourceUrl
  )

  const ctaEbookLink = getCtaEbookLpUrl(isSidebarCta, educationCategory.id, slug)

  // design for standard desktop width
  if (isCtaForGuidePage) {
    return (
      <Block
        className="purple-gradient-bg with-box-shadow"
        columns={2}
        columnsTemplate="1fr 17%"
        css={guidePageCtaStyle}
      >
        <div>
          {isMobile() ? (
            <h3 className="heading3 no-margin">
              Too busy? <br /> Download now & read later.
            </h3>
          ) : (
            <h2 className="heading2 no-margin">
              Too busy? <br /> Download now & read later.
            </h2>
          )}
          <Button link={`/resources/ebooks/${slug}/`} css={guidePageCtaButtonStyle}>
            Get the PDF
          </Button>
        </div>
        {isMobile(breakpoints.small) ? (
          <div css={iconStyle}>
            <Image src="cta/ebook-small.webp" alt={`airfocus eBook ${title}`} />
          </div>
        ) : (
          <Link
            className="scale-on-hover with-blue-box-shadow"
            href={`/resources/ebooks/${slug}/`}
            css={guidePageImageStyle}
          >
            <GatsbyImage
              image={ctaImage.gatsbyImageData}
              alt={`airfocus eBook ${title}`}
              imgStyle={{ borderRadius: '0 0.5rem 0.5rem 0' }}
            />
          </Link>
        )}
      </Block>
    )
  }

  // default return
  return (
    <div className="purple-bg with-box-shadow" css={[containerStyle(isSidebarCta), cssStyles]}>
      {isSidebarCta && (
        <Link className="scale-on-hover" href={ctaEbookLink} css={imageStyle(isSidebarCta)}>
          <GatsbyImage
            image={ctaImage.gatsbyImageData}
            alt={`airfocus eBook ${title}`}
            imgStyle={{ borderRadius: '0 0.5rem 0.5rem 0' }}
          />
        </Link>
      )}
      <div css={contentStyle(isSidebarCta)}>
        <div>
          <div className={`blue-text ${isSidebarCta ? 'strong-body-2' : 'heading4'}`} css={blueTextStyle(isSidebarCta)}>
            {getSingularForm(educationCategory.name)}
          </div>
          {isSidebarCta ? (
            <div className="cta-sidebar-title black-text">{ctaTitle ? parse(ctaTitle) : title}</div>
          ) : (
            <h3 className="heading3 black-text no-margin">{ctaTitle ? parse(ctaTitle) : title}</h3>
          )}
        </div>
        <Button link={ctaEbookLink} css={buttonStyle(isSidebarCta)}>
          Read now
        </Button>
      </div>
      {!isSidebarCta && !isMobile(breakpoints.xlarge) && (
        <div css={paperPlaneImageStyle}>
          <Image src="cta/paper-plane-ebook.webp" alt="CTA eBook image background" />
        </div>
      )}
      {!isSidebarCta &&
        ((isMobile(ctaInlineBreakPoint) && !isMobile(breakpoints.medium)) || isMobile(breakpoints.small) ? (
          <div css={iconStyle}>
            <Image src="cta/ebook-small.webp" alt={`airfocus eBook ${title}`} />
          </div>
        ) : (
          <Link
            className={isMobile(breakpoints.xlarge) ? 'scale-on-hover' : undefined}
            href={ctaEbookLink}
            css={imageStyle(isSidebarCta)}
          >
            <GatsbyImage
              image={ctaImage.gatsbyImageData}
              alt={`airfocus eBook ${title}`}
              imgStyle={{ borderRadius: '0 0.5rem 0.5rem 0' }}
            />
          </Link>
        ))}
    </div>
  )
}

export default CTAEbook

const guidePageCtaStyle = css`
  margin-top: 4rem;
  border-radius: 0.5rem;
  padding: 3rem 5rem;

  @media screen and (max-width: ${longCopyBreakPoint}px) {
    padding: 3rem;
    grid-template-columns: 1fr 19%;
  }

  @media screen and (max-width: ${longCopyBreakPoint}px) {
    padding: 2.5rem 7%;
    grid-template-columns: 1fr 19%;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: 2rem;
    grid-template-columns: 1fr;
  }
`

const guidePageCtaButtonStyle = css`
  margin-top: 3rem;

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-top: 1rem;
    max-width: 100%;
  }
`

const guidePageImageStyle = css`
  border-radius: 0 0.5rem 0.5rem 0;
  overflow: hidden;
`

const containerStyle = (isSidebarCta: boolean) => css`
  // richt-text (center column) always has a slight margin-top for which we need to align
  ${isSidebarCta ? 'margin-top: 0.25rem' : 'margin: 2rem 0'};
  ${isSidebarCta ? 'position: sticky; top: 6rem;' : 'position: relative;'};
  border-radius: 0.5rem;
  padding: ${isSidebarCta ? '1.25rem' : '2rem'};
  overflow: hidden;
  min-height: 9rem;
  // stretch content div to 100% height & for tablet / mobile design
  ${!isSidebarCta &&
  `
    display: flex;

    @media screen and (max-width: ${breakpoints.xlarge}px) {
      justify-content: space-between;
      overflow: visible;
      min-height: initial;
      padding: 2rem 3rem;
    }

    @media screen and (max-width: ${breakpoints.small}px) {
      display: block;
      padding: 2rem;
    }
  `};

  @media screen and (max-width: ${ctaInlineBreakPoint}px) and (min-width: ${breakpoints.medium + 1}px),
    (max-width: ${breakpoints.small}px) {
    margin-top: 3rem;
  }
`

const contentStyle = (isSidebarCta: boolean) => css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;

  ${!isSidebarCta &&
  `
    @media screen and (min-width: ${breakpoints.xlarge}px) {max-width: 60%};
    @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoints.medium}px) {max-width: 68%};
  `};
`

// export for CTATemplates
export const blueTextStyle = (isSidebarCta: boolean) => css`
  ${isSidebarCta && 'margin: 0.75rem 0 0.25rem'};
`

const buttonStyle = (isSidebarCta: boolean) => css`
  margin-top: ${isSidebarCta ? '1rem' : '1.5rem'};
  ${isSidebarCta && 'width: 100%'};
`

const paperPlaneImageStyle = css`
  position: absolute;
  width: 325px;
  right: -2px;
  bottom: -1px;

  @media screen and (max-width: ${longCopyBreakPoint}px) {
    width: 241px;
  }
`

const imageStyle = (isSidebarCta: boolean) => css`
  display: block;
  border-radius: 0 0.5rem 0.5rem 0;

  ${isSidebarCta
    ? 'max-width: 150px;'
    : `
    // hover effect: #3146ff (12.5%) box-shadow only for inlineCTA with paper-plane-bg (screen width > breakpoints.xlarge)
    transition: all 0.3s ease-in-out;
    &:hover {
      box-shadow: rgb(49, 70, 255, 0.125) 0 0.2rem 2rem 1rem;
    }
    
    @media screen and (min-width: ${breakpoints.xlarge + 1}px) {
      position: absolute;
      width: 157px;
      height: 203px;
      bottom: -41px;
      right: 58px;
    }
    
    @media screen and (min-width: ${ctaInlineBreakPoint + 1}px) and (max-width: ${breakpoints.xlarge}px),
      (max-width: ${breakpoints.medium}px) {
      margin: auto 0 auto 2rem;
      width: 100%;
      max-width: 8rem;
    }
  `}

  @media screen and (max-width: ${breakpoints.medium}px) {
    width: 145px;
  }
`

const iconStyle = css`
  width: 4rem;
  position: absolute;
  top: -2rem;
  right: 2rem;
`
